import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Package, AlertTriangle, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { get, post, patch } from "@/lib/api-client";
import { Card, CardBody } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { StatusPill } from "@/components/ui/StatusPill";
import { Skeleton } from "@/components/ui/Skeleton";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { EmptyState } from "@/components/ui/EmptyState";

interface InventoryRow {
  variant_id: number;
  variant_sku: string;
  product_name: string;
  available: number;
  reserved: number;
  sold: number;
  status: string;
  low_stock_threshold: number;
}

export function InventoryPage() {
  const [restock, setRestock] = useState<InventoryRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InventoryRow | null>(null); // 🛠️ NEW
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-inventory"],
    queryFn: () => get<InventoryRow[]>("/admin/inventory"),
  });

  const restockMutation = useMutation({
    mutationFn: (d: { variant_id: number; quantity: number; reason: string }) =>
      post<void>(`/admin/inventory/${d.variant_id}/restock`, { quantity: d.quantity, reason: d.reason }),
    onSuccess: () => {
      toast.success("Stock restocked");
      setRestock(null); setQty(""); setReason("");
      qc.invalidateQueries({ queryKey: ["admin-inventory"] });
    },
  });

  // 🛠️ NEW: Deactivate Variant Mutation
  const deleteVariant = useMutation({
    mutationFn: (variant_id: number) => patch(`/admin/variants/${variant_id}`, { status: "inactive" }),
    onSuccess: () => {
      toast.success("Variant deactivated successfully");
      qc.invalidateQueries({ queryKey: ["admin-inventory"] });
      setDeleteTarget(null);
    },
  });

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-10">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Inventory Management</h1>
        <p className="text-sm text-surface-500 mt-1">Real-time stock levels. Deleting an item removes it from sale.</p>
      </header>

      <Card>
        <CardBody className="p-0">
          {isLoading ? <div className="p-5"><Skeleton className="h-14 w-full" /></div> : data && data.length ? (
            <Table>
              <THead>
                <TR><TH>SKU</TH><TH>Product</TH><TH className="text-center">Available</TH><TH className="text-center">Reserved</TH><TH className="text-center">Sold</TH><TH>Status</TH><TH className="text-right">Actions</TH></TR>
              </THead>
              <tbody>
                {data.map((row) => (
                  <TR key={row.variant_id}>
                    <TD className="font-mono text-xs">{row.variant_sku}</TD>
                    <TD className="font-bold">{row.product_name}</TD>
                    <TD className="text-center font-bold text-lg">
                      {row.available}
                      {row.available <= row.low_stock_threshold && row.available > 0 && <AlertTriangle className="inline h-4 w-4 text-warning-600 ml-1" />}
                    </TD>
                    <TD className="text-center text-surface-500">{row.reserved}</TD>
                    <TD className="text-center text-surface-500 font-semibold">{row.sold}</TD>
                    <TD><StatusPill status={row.status} /></TD>
                    <TD className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" leftIcon={<Plus className="w-3.5 h-3.5" />} onClick={() => setRestock(row)}>Restock</Button>
                        <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(row)}><Trash2 className="w-4 h-4 text-danger-500 hover:text-danger-600"/></Button>
                      </div>
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          ) : <EmptyState icon={Package} title="No inventory records yet" /> }
        </CardBody>
      </Card>

      {restock && (
        <Modal open onClose={() => setRestock(null)} title={`Restock ${restock.variant_sku}`}>
          <div className="space-y-4">
            <Input label="Quantity to add" type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} />
            <Input label="Reason (audited)" value={reason} onChange={(e) => setReason(e.target.value)} />
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setRestock(null)}>Cancel</Button>
              <Button loading={restockMutation.isPending} onClick={() => restockMutation.mutate({ variant_id: restock.variant_id, quantity: Number(qty), reason })}>Restock</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* 🛠️ NEW: Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Stock Item?"
        message={`Are you sure you want to delete SKU "${deleteTarget?.variant_sku}"? This will deactivate the variant and hide it from customers.`}
        confirmLabel="Delete Item"
        destructive
        loading={deleteVariant.isPending}
        onConfirm={() => { if (deleteTarget) deleteVariant.mutate(deleteTarget.variant_id); }}
      />
    </div>
  );
}